/**
 * TurnIndicator Component
 *
 * Displays whose turn it is in the game.
 * Shows active state when it's the player's turn and inactive when waiting.
 *
 * Props:
 * - currentTurn: ID of player whose turn it is
 * - playerId: ID of the current player viewing the indicator
 * - myHasCapturedBonus: Whether current player captured bonus piece (Phase 7.9.4.1)
 * - opponentHasCapturedBonus: Whether opponent captured bonus piece (Phase 7.9.4.1)
 */

import { COPY } from '../copy';
import BonusCapturedBadge from './BonusCapturedBadge';

export default function TurnIndicator({
  currentTurn,
  playerId,
  myHasCapturedBonus = false,
  opponentHasCapturedBonus = false,
}) {
  // Determine if it's the player's turn
  const isMyTurn = currentTurn === playerId;

  // Handle edge cases
  if (!currentTurn || !playerId) {
    return (
      <div
        data-testid="turn-indicator"
        aria-live="polite"
        aria-label="Turn indicator"
        role="status"
        className="text-center p-4 rounded-lg bg-dark-purple border-2 border-gray-600 text-gray-400"
      >
        <p className="text-lg">Waiting...</p>
      </div>
    );
  }

  // Determine styling based on turn state
  const styleClasses = isMyTurn
    ? 'bg-dark-purple border-neon-pink shadow-glow-pink text-neon-pink animate-pulse-glow'
    : 'bg-dark-purple border-neon-cyan/30 text-gray-400';

  const fontWeightClass = isMyTurn ? 'font-bold' : 'font-normal';

  const turnText = isMyTurn ? COPY.turn.yourTurn : COPY.turn.opponentTurn;

  // Phase 7.9.4.1: Determine which player has captured bonus
  const currentPlayerHasBonus = isMyTurn
    ? myHasCapturedBonus
    : opponentHasCapturedBonus;

  return (
    <div
      data-testid="turn-indicator"
      aria-live="polite"
      aria-label={turnText}
      role="status"
      className={`text-center p-4 rounded-lg border-2 ${styleClasses} ${fontWeightClass} transition-all duration-300`}
    >
      <div className="flex items-center justify-center gap-2">
        <p className="text-lg">{turnText}</p>
        <BonusCapturedBadge hasCapturedBonus={currentPlayerHasBonus} />
      </div>
    </div>
  );
}
